/**
 * Created by Andrey Popov on 07.08.26
 */

// Counts "joined" friends the way Match The Cars does (fb app 1769176524477982, SDK 8.0 bundle read 01.09.2026):
// connector.platform.checkInvitees() - the player's friends who play THIS game (Instant: getConnectedPlayersAsync, ids only
// under zero permissions, so the congrat shows random avatars) - is rechecked on start, every session and when the window
// opens; every id not yet in data.connected is a new friend and earns the next slot of a fixed track. A friend who found
// the game on their own counts too; the invite itself is never verified - trackArrive only logs the arrival of
// players who came through our link (payload.invitedBy).
const InviteFriends = function () {
    cleverapps.EventEmitter.call(this);

    this.load();
};

InviteFriends.prototype = Object.create(cleverapps.EventEmitter.prototype);
InviteFriends.prototype.constructor = InviteFriends;

// the synced storage is replaced when a server snapshot arrives - reload on userChanged (see InitByFeature)
InviteFriends.prototype.load = function () {
    this.data = cleverapps.syncedStorage.load("invitefriends") || {};
};

InviteFriends.prototype.save = function () {
    cleverapps.syncedStorage.save("invitefriends", this.data);
};

InviteFriends.prototype.reset = function () {
    this.data = {};
    cleverapps.syncedStorage.remove("invitefriends");

    this.onChanged();
};

InviteFriends.prototype.getJoined = function () {
    return Math.min((this.data.connected || []).length - (this.data.skipped || 0), InviteFriends.SLOTS_COUNT);
};

InviteFriends.prototype.getRewarded = function () {
    return this.data.rewarded || 0;
};

InviteFriends.prototype.isAvailable = function () {
    // without a real checkInvitees, joined friends can never be counted - fall back to the plain share-only InviteIcon
    return !cleverapps.gameModes.noInviteFriends && connector.social.canCheckInvitees() && cleverapps.meta.getSelectedLocation().locationConfig.isMain
        && levels.user.checkAvailable(cleverapps.config.availables.INVITE_FRIENDS) && this.getRewarded() < InviteFriends.SLOTS_COUNT;
};

InviteFriends.prototype.isSettled = function () {
    const loginPending = !connector.social.isConnected() || connector.social.isLoggedIn() && PlatformLogic.isTmpId(connector.platform.getUserID());
    return !loginPending && [connector.SYNC_STATUS.IDLE, connector.SYNC_STATUS.DISABLE].includes(connector.synchronizer.status);
};

InviteFriends.prototype.hasUnclaimedRewards = function () {
    return this.isSettled() && this.getRewarded() < this.getJoined();
};

InviteFriends.prototype.syncConnected = function (callback) {
    callback = callback || function () {};

    if (!this.isAvailable() || !this.isSettled()) {
        callback();
        return;
    }

    connector.platform.checkInvitees((code, ids) => {
        if (code === connector.CODE_SUCCEED && this.isSettled()) {
            this.addJoined(ids, this.data.connected === undefined);
        }

        callback();
    });
};

// ids of friends who play the game; each new one is counted once
InviteFriends.prototype.addJoined = function (ids, skip) {
    const connected = this.data.connected || [];
    const own = connector.platform.getUserID();

    const fresh = ids.map((id) => (id === undefined || id === null ? "" : String(id)))
        .filter((id, index, list) => id && id !== own && !connected.includes(id) && list.indexOf(id) === index);

    if (!fresh.length && !skip) {
        return;
    }

    this.data.connected = connected.concat(fresh);
    if (skip) {
        this.data.skipped = fresh.length;
    }
    this.save();

    if (!skip) {
        fresh.forEach(() => {
            cleverapps.metrics.logEvent(cleverapps.EVENTS.INVITE_FRIENDS_JOINED);
        });
    }

    this.onChanged();
};

// marks the pending slots as rewarded and returns their rewards; the caller shows them
// in a RewardWindow (which receives them) - see displayCongrat, the only claiming path
InviteFriends.prototype.claimPending = function () {
    const joined = this.getJoined();
    const rewarded = this.getRewarded();

    if (!this.isSettled() || rewarded >= joined) {
        return undefined;
    }

    const units = [];
    for (let i = rewarded; i < joined; i++) {
        units.push(UnitsLibraryHelper.ResolveUnit(InviteFriendsConfig.SLOTS[i].units));

        cleverapps.metrics.logEvent(`${cleverapps.EVENTS.INVITE_FRIENDS_REWARD}_${i + 1}`);
    }

    this.data.rewarded = joined;
    this.save();

    this.onChanged();

    return new RewardsList({ units }, { event: cleverapps.EVENTS.EARN.INVITE_FRIENDS });
};

InviteFriends.prototype.onChanged = function () {
    this.trigger("changed");

    if (cleverapps.sideBar) {
        cleverapps.sideBar.resetByClassName(InviteFriendsIcon);
        cleverapps.sideBar.resetByClassName(InviteIcon);
    }
};

// logs the one-time arrival of a player who came through our invite link; joining itself is
// counted on the inviter's side from the platform friend list (syncConnected)
InviteFriends.prototype.trackArrive = function () {
    connector.platform.parsePayload((payload) => {
        const invitedBy = payload && payload.invitedBy;

        if (!invitedBy || invitedBy === connector.platform.getUserID()) {
            return;
        }

        if (!cleverapps.config.debugMode) {
            if (this.data.arriveLogged || !cleverapps.user.isFirstSession()) {
                return;
            }
        }

        this.data.arriveLogged = true;
        this.save();

        cleverapps.metrics.logEvent(cleverapps.EVENTS.INVITE_FRIENDS_ARRIVE);
    });
};

InviteFriends.prototype.invite = function () {
    cleverapps.metrics.logEvent(cleverapps.EVENTS.INVITE_FRIENDS_CLICK);
    this.clickedThisWindow = true;

    cleverapps.SocialManager.inviteFriends("invite", (success) => {
        if (success) {
            cleverapps.metrics.logEvent(cleverapps.EVENTS.INVITE_FRIENDS_SENT);
        }
    }, { invitedBy: connector.platform.getUserID() });
};

InviteFriends.prototype.displayWindow = function () {
    if (this.hasUnclaimedRewards()) {
        this.displayCongrat();
        return;
    }

    cleverapps.focusManager.display({
        focus: "InviteFriendsWindow",
        action: function (f) {
            new InviteFriendsWindow();
            cleverapps.focusManager.onceNoWindowsListener = f;
        }
    });
};

InviteFriends.prototype.windowClosed = function () {
    this.data.next = Date.now() + cleverapps.parseInterval(this.clickedThisWindow ? InviteFriends.REPEAT_AFTER_INVITE : InviteFriends.REPEAT_AFTER_DISMISS);
    this.save();
};

InviteFriends.prototype.needRepeat = function () {
    return Boolean(this.data.introShown && this.data.next) && Date.now() >= this.data.next && this.isAvailable() && !this.hasUnclaimedRewards()
        && connector.platform.listShareChannels().length > 0;
};

InviteFriends.prototype.needIntro = function () {
    return !this.data.introShown && this.isAvailable() && connector.platform.listShareChannels().length > 0;
};

InviteFriends.prototype.createGuideWindow = function () {
    return new GuideWindow({
        name: "InviteFriendsRulesWindow",
        bundle: bundles.invite_friends_guide_window
    });
};

InviteFriends.prototype.displayIntro = function () {
    cleverapps.focusManager.display({
        focus: "InviteFriendsIntro",
        actions: [
            (f) => {
                this.data.introShown = true;
                this.save();

                this.createGuideWindow();
                cleverapps.focusManager.onceNoWindowsListener = f;
            },
            (f) => {
                new InviteFriendsWindow();
                cleverapps.focusManager.onceNoWindowsListener = f;
            }
        ]
    });
};

InviteFriends.prototype.displayCongrat = function () {
    cleverapps.metrics.logEvent(cleverapps.EVENTS.INVITE_FRIENDS_CONGRAT);

    cleverapps.focusManager.display({
        focus: "InviteFriendsCongrat",
        actions: [
            (f) => {
                new InviteFriendsCongratWindow();
                cleverapps.focusManager.onceNoWindowsListener = f;
            },
            (f) => {
                const rewards = this.claimPending();
                if (!rewards) {
                    f();
                    return;
                }

                new RewardWindow(rewards, { event: cleverapps.EVENTS.EARN.INVITE_FRIENDS });
                cleverapps.focusManager.onceNoWindowsListener = f;
            },
            (f) => {
                new InviteFriendsWindow();
                cleverapps.focusManager.onceNoWindowsListener = f;
            }
        ]
    });
};

InviteFriends.SLOTS_COUNT = InviteFriendsConfig.SLOTS.length;
InviteFriends.REPEAT_AFTER_DISMISS = "5 days";
InviteFriends.REPEAT_AFTER_INVITE = "14 days";

cleverapps.InitByFeature["invite_friends"] = function () {
    cleverapps.inviteFriends = new InviteFriends();

    cleverapps.inviteFriends.trackArrive();

    cleverapps.eventBus.on("userChanged", () => {
        cleverapps.inviteFriends.load();
        cleverapps.inviteFriends.onChanged();
    });

    connector.platform.whenConnected(() => {
        cleverapps.inviteFriends.syncConnected();
    });
    connector.social.whenConnected(() => {
        cleverapps.inviteFriends.syncConnected();
    });
    connector.synchronizer.on("changeStatus", (status) => {
        if (status === connector.SYNC_STATUS.IDLE) {
            cleverapps.inviteFriends.load();
            cleverapps.inviteFriends.onChanged();
            cleverapps.inviteFriends.syncConnected();
        }
    });
    connector.metrics.on("startSession", () => {
        cleverapps.inviteFriends.syncConnected();
    });
};

Placements.ENTRIES.inviteFriendsIntro = {
    type: Placements.FREE_FOCUS_MAIN,

    filter: function () {
        return Boolean(cleverapps.inviteFriends) && cleverapps.inviteFriends.needIntro();
    },

    action: function () {
        cleverapps.inviteFriends.displayIntro();
    }
};

Placements.ENTRIES.inviteFriendsRepeat = {
    throttled: true,

    type: Placements.FREE_FOCUS_MAIN,

    filter: function () {
        return Boolean(cleverapps.inviteFriends) && cleverapps.inviteFriends.needRepeat();
    },

    action: function () {
        cleverapps.inviteFriends.displayWindow();
    }
};

Placements.ENTRIES.inviteFriendsCongrat = {
    throttled: true,

    type: Placements.FREE_FOCUS_MAIN,

    filter: function () {
        return Boolean(cleverapps.inviteFriends) && cleverapps.inviteFriends.isAvailable() && cleverapps.inviteFriends.hasUnclaimedRewards();
    },

    action: function () {
        cleverapps.inviteFriends.displayCongrat();
    }
};
